import type { AnyRecord } from './records/types';
import type { Nullable } from './values';
import { hasValue } from './values';

// TODO: split all the util functions into separate files for tree-shaking.

/** Returns a new object that shallow copies the selected properties. */
export function pick<T extends AnyRecord, K extends keyof T>(record: T, ...keys: K[]): { [P in K]: T[P] } {
  const result = {} as { [P in K]: T[P] };
  for (const key of keys) {
    result[key] = record[key];
  }
  return result;
}

export type GetKey<R> = (item: R) => string;
export type FilterMapCallback<T, V> = (value: T) => Nullable<V>;

export function mapToRecord<T, K extends string>(list: T[], getKey: GetKey<T>): Record<K, T>;
export function mapToRecord<T, V, Key extends string>(
  list: T[],
  getKey: GetKey<T>,
  getValue: FilterMapCallback<T, V>
): Record<Key, V>;
/**
 * Maps an array of items to a record (object) using a provided key-generating function.
 * Optionally, a map function can be provided to transform the values.
 * If the map function returns null or undefined, the item will be excluded from the record.
 */
export function mapToRecord<T, K = T>(list: T[], getKey: GetKey<T>, map?: FilterMapCallback<T, K>): Record<string, K> {
  const record: Record<string, T | K> = {};

  for (const item of list) {
    const key = getKey(item);
    const value = map ? map(item) : item;

    if (hasValue(value)) {
      record[key] = value;
    }
  }

  return record as Record<string, K>;
}
